// Support (reachable signed out), Privacy and Terms of the console (the legacy text, unchanged).
import { useSession } from '@qor/web-shared';
import { useQuery } from '@tanstack/react-query';
import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../api';
import { usePageTitle } from '../components/ConsoleLayout';

const tileTitle = {
  fontSize: '.82rem',
  textTransform: 'uppercase',
  letterSpacing: '.03em',
  color: 'var(--qor-muted)',
} as const;
const tileValue = { fontSize: '1.3rem', fontWeight: 900, margin: '0 0 6px' } as const;
const tileHint = { color: 'var(--qor-muted)', fontSize: '.82rem', margin: 0 } as const;

export function SupportPage() {
  usePageTitle('Support');
  const signedIn = !!useSession().data?.user;
  // the processor probes are for signed-in staff; signed out, the tile says so instead of "degraded"
  const health = useQuery({
    queryKey: ['support', 'health'],
    enabled: signedIn,
    queryFn: async () => {
      const probes = await Promise.all([adminApi.probe('mdp'), adminApi.probe('intercash')]);
      return probes.filter((p) => !(p?.reachable ?? false)).length;
    },
  });
  const unreachable = health.data;

  return (
    <>
      <div className="qc-page-heading-row">
        <div>
          <h1 style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            Support &amp; Helpdesk <span className="qc-badge qc-badge-accent">Self-Service Center</span>
          </h1>
          <p className="qc-scope-label" style={{ marginTop: 6 }}>
            Self-service runbooks, common administrative workflows &amp; escalation channels
          </p>
        </div>
      </div>

      <section className="qc-stat-row" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', marginBottom: 24 }}>
        <div className="qc-panel">
          <div className="qc-panel-title">
            <h3 style={tileTitle}>Active System Health</h3>
          </div>
          <p
            style={{
              ...tileValue,
              color:
                unreachable === undefined ? undefined : unreachable === 0 ? 'var(--qor-success)' : 'var(--qor-red)',
            }}
          >
            {!signedIn
              ? 'Sign in to see system health'
              : unreachable === undefined
                ? ''
                : unreachable === 0
                  ? 'All Systems Operational'
                  : `${unreachable} System(s) Degraded`}
          </p>
          <p style={tileHint}>MDP &amp; InterCash processor gateways</p>
        </div>
        <div className="qc-panel">
          <div className="qc-panel-title">
            <h3 style={tileTitle}>Self-Service Runbooks</h3>
          </div>
          <p style={tileValue}>4 Available</p>
          <p style={tileHint}>Console operations you can run yourself</p>
        </div>
        <div className="qc-panel">
          <div className="qc-panel-title">
            <h3 style={tileTitle}>Support Contact</h3>
          </div>
          <p style={tileValue}>
            <a href="mailto:support@qorteams.com">support@qorteams.com</a>
          </p>
          <p style={tileHint}>Operations helpdesk</p>
        </div>
      </section>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', marginBottom: '4px' }}>
        <h2 style={{ margin: '0', fontSize: '1.1rem' }}>Things you can do yourself</h2>
        <span className="qc-badge qc-badge-accent">4 Standard Runbooks</span>
      </div>
      <p className="qc-scope-label" style={{ textTransform: 'none', letterSpacing: 'normal', marginBottom: '16px' }}>
        Automated console operations with instantaneous execution and audit trails.
      </p>

      <div className="qc-runbook-grid">
        <div className="qc-panel qc-runbook-card">
          <div className="qc-runbook-card-head">
            <strong>Need to reset a staff member's password?</strong>
            <span className="qc-badge qc-badge-muted">Users</span>
          </div>
          <p>
            Instantly reset or dispatch credentials for any console staff member without contacting external support.
          </p>
          <code className="qc-code-chip">Path: Users &rarr; Select User &rarr; &quot;Set Password&quot;</code>
          <div className="qc-runbook-card-foot">
            <span>Self-service clearance: Tier 1+</span>
            <Link to="/users">
              Go to Users Console <span aria-hidden="true">&rarr;</span>
            </Link>
          </div>
        </div>

        <div className="qc-panel qc-runbook-card">
          <div className="qc-runbook-card-head">
            <strong>A cardholder reports their card lost or stolen?</strong>
            <span className="qc-badge qc-badge-muted">Cards</span>
          </div>
          <p>
            Cardholders can report directly via their portal, or administrators can immediately freeze, terminate, or
            reissue active tokens.
          </p>
          <code className="qc-code-chip">Path: Cardholders &rarr; Cardholder Detail &rarr; Card Status</code>
          <div className="qc-runbook-card-foot">
            <span>Self-service clearance: Operator</span>
            <Link to="/cardholders">
              Cardholder Management <span aria-hidden="true">&rarr;</span>
            </Link>
          </div>
        </div>

        <div className="qc-panel qc-runbook-card">
          <div className="qc-runbook-card-head">
            <strong>Need to review who did what?</strong>
            <span className="qc-badge qc-badge-muted">Audit</span>
          </div>
          <p>
            Every administrative action, balance modification, and parameter update is recorded with identity stamps.
          </p>
          <code className="qc-code-chip">Path: Audit Log &rarr; Filter by Actor / Target</code>
          <div className="qc-runbook-card-foot">
            <span>Full activity log</span>
            <Link to="/audit-log">
              View Audit Log <span aria-hidden="true">&rarr;</span>
            </Link>
          </div>
        </div>

        <div className="qc-panel qc-runbook-card">
          <div className="qc-runbook-card-head">
            <strong>Forgot your own password?</strong>
            <span className="qc-badge qc-badge-muted">Recovery</span>
          </div>
          <p>
            Select &quot;Forgot password?&quot; on the sign-in page. A single-use verification code is sent to your
            account email.
          </p>
          <code className="qc-code-chip">Method: Self-serve sign-in verification code</code>
          <div className="qc-runbook-card-foot">
            <span>Email verification</span>
            <Link to="/forgot-password">
              Reset Password <span aria-hidden="true">&rarr;</span>
            </Link>
          </div>
        </div>
      </div>

      <div className="qc-panel" style={{ maxWidth: '720px', marginTop: '24px' }}>
        <div className="qc-panel-title">
          <h3>Still need help?</h3>
          <span className="qc-badge qc-badge-accent">Primary</span>
        </div>
        <p className="qc-empty" style={{ margin: '0 0 10px' }}>
          Direct inbox, monitored during business hours. Email us and we&apos;ll get back to you as soon as possible.
        </p>
        <p style={{ margin: '0' }}>
          <a href="mailto:support@qorteams.com">support@qorteams.com</a>
        </p>
      </div>
    </>
  );
}

function LegalDoc({ title, children }: { title: string; children: ReactNode }) {
  usePageTitle(title);
  return (
    <>
      <div className="qc-page-heading">
        <h1>{title}</h1>
        <p className="qc-breadcrumb">Last updated: September 2026</p>
      </div>
      <div className="qc-panel qc-legal-doc" style={{ maxWidth: 820 }}>
        {children}
      </div>
    </>
  );
}

export function PrivacyPage() {
  return (
    <LegalDoc title="Privacy Policy">
      <p>
        This Privacy Policy explains how QOR Platform (&quot;QOR,&quot; "we," "us") collects, uses, and protects
        information handled through this administrative console, including cardholder and staff account data.
      </p>
      <h3>Information we collect</h3>
      <p>
        This console gives authorized program staff access to cardholder profile information, transaction and card
        status data, and platform user (staff) accounts. We do not collect or store full card numbers, expiration dates,
        or CVV on this platform -- that data is held exclusively by our card-issuing partners under their own security
        controls.
      </p>
      <h3>How we use your information</h3>
      <p>
        Staff account information is used to authenticate access, enforce role-based permissions, and maintain an audit
        trail of administrative actions. Cardholder information accessed through this console is used solely to
        administer card programs -- onboarding, support, and compliance obligations owed to our card-issuing partners.
      </p>
      <h3>Who we share it with</h3>
      <p>
        We share the minimum information necessary with the card-issuing partner backing each program, and with service
        providers who help us operate the platform (such as email delivery). We do not sell personal information. We may
        disclose information where required by law or as part of a standard compliance review by a card-issuing partner.
      </p>
      <h3>How we protect information</h3>
      <p>
        Access to this console requires authentication and is scoped to the Administrator role. Every action that
        changes account access, cardholder status, or funds is logged with the acting staff member&apos;s identity,
        visible on the Audit Log page. Passwords are never stored in plain text.
      </p>
      <h3>Contact us</h3>
      <p>
        Questions about this policy can be sent to the contact listed on our <Link to="/support">Support page</Link>.
      </p>
    </LegalDoc>
  );
}

export function TermsPage() {
  return (
    <LegalDoc title="Terms of Service">
      <p>
        These Terms of Service (&quot;Terms&quot;) govern staff use of the QOR Platform administrative console. By
        signing in, you agree to these Terms.
      </p>
      <h3>Your account</h3>
      <p>
        Access to this console is granted per staff member and scoped to a specific role. You&apos;re responsible for
        keeping your login credentials confidential and for all activity that occurs under your account. If you believe
        your account has been accessed without your permission, change your password immediately and notify your program
        administrator.
      </p>
      <h3>Acceptable use</h3>
      <p>
        This console is for authorized program administration only: managing cardholders, reviewing transactions, and
        configuring program settings within the scope of your role. Every action you take that changes account access,
        cardholder status, or funds is recorded in the Audit Log with your identity attached. You agree not to access
        cardholder or account data outside the scope of your duties.
      </p>
      <h3>Confidentiality</h3>
      <p>
        Cardholder information accessed through this console is confidential and may only be used for legitimate program
        administration purposes. It may not be exported, shared, or disclosed except as required to perform your role or
        as required by law.
      </p>
      <h3>Changes to these Terms</h3>
      <p>
        We may update these Terms from time to time. Continued use of the console after an update constitutes acceptance
        of the revised Terms.
      </p>
      <h3>Contact us</h3>
      <p>
        Questions about these Terms can be sent to the contact listed on our <Link to="/support">Support page</Link>.
      </p>
    </LegalDoc>
  );
}
